import axios from 'axios';
import { captureHttpErrorIfNeeded } from 'shared/sentry';
import { AUTH_TOKEN_KEYS, TIMEOUT_CONFIG } from '../constants/common';
import { API_BASE_URL } from '../constants/api';
import authService from '../services/authService';
import { logoutUser } from '../actions/authActions';

/**
 * HTTP status codes for error handling
 */
const HTTP_STATUS = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
};

/**
 * Error messages for different HTTP status codes
 */
const ERROR_MESSAGES = {
  [HTTP_STATUS.BAD_REQUEST]: 'Bad request. Please check your input.',
  [HTTP_STATUS.NOT_FOUND]: 'Resource not found.',
  [HTTP_STATUS.INTERNAL_SERVER_ERROR]: 'Internal server error. Please try again later.',
  [HTTP_STATUS.BAD_GATEWAY]: 'Server is temporarily unavailable. Please try again later.',
  [HTTP_STATUS.SERVICE_UNAVAILABLE]: 'Service is temporarily unavailable. Please try again later.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  DEFAULT: 'An unexpected error occurred.',
};

/**
 * Create axios instance for API calls
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: TIMEOUT_CONFIG.DEFAULT, // Default 10 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Handle authentication failure by clearing tokens and redirecting to login
 * @param {Function} dispatch - Redux dispatch function (optional)
 */
const handleAuthFailure = (dispatch) => {
  authService.clearTokens();
  
  if (dispatch) {
    dispatch(logoutUser());
  }
  
  const currentPath = window.location.pathname;
  if (currentPath !== '/login' && currentPath !== '/register' && currentPath !== '/creator-signup') {
    window.location.href = '/login';
  }
};

/**
 * Handle HTTP errors and set appropriate error messages
 * @param {Object} error - Axios error object
 * @returns {Object} Enhanced error object
 */
const handleHttpError = (error) => {
  if (!error.response) {
    error.message = ERROR_MESSAGES.NETWORK_ERROR;
    return error;
  }

  const status = error.response.status;
  const serverMessage = error.response.data?.message;
  
  if (serverMessage) {
    error.message = serverMessage;
  } else if (ERROR_MESSAGES[status]) {
    error.message = ERROR_MESSAGES[status];
  } else {
    error.message = ERROR_MESSAGES.DEFAULT;
  }
  
  return error;
};

/**
 * Request interceptor to add authentication token and cache prevention
 */
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem(AUTH_TOKEN_KEYS.ACCESS_TOKEN);
    
    if (accessToken) {
      config.headers.Authorization = `${accessToken}`;
    }
    
    // Add timezone header in IANA timezone format (e.g., "America/New_York", "Europe/London")
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    config.headers['X-Timezone'] = timezone;
    
    // Preserve existing params for GET requests
    if (config.method === 'get') {
      config.params = {
        ...config.params,
      };
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor to handle errors and authentication failures
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    
    captureHttpErrorIfNeeded(error, {
      tags: {
        errorType: 'axios_error',
        httpStatus: status || 'network_error',
      },
      contexts: {
        http: {
          method: error.config?.method?.toUpperCase(),
          url: error.config?.url,
          status_code: status,
          request_headers: error.config?.headers,
        },
      },
      extra: {
        responseData: error.response?.data,
        requestData: error.config?.data,
      },
    });

    // Handle authentication errors
    if (status === HTTP_STATUS.UNAUTHORIZED || status === HTTP_STATUS.FORBIDDEN) {
      handleAuthFailure(null);
      return Promise.reject(error);
    }
    
    // Handle other HTTP errors
    const enhancedError = handleHttpError(error);
    return Promise.reject(enhancedError);
  }
);

/**
 * HTTP methods supported by the API client
 */
const HTTP_METHODS = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  PATCH: 'patch',
  DELETE: 'delete',
};

/**
 * Centralized API request handler with standardized response format
 * @param {string} method - HTTP method (GET, POST, PUT, PATCH, DELETE)
 * @param {string} url - API endpoint URL
 * @param {Object|null} data - Request data (null for GET requests)
 * @param {Object} config - Additional axios configuration
 * @returns {Promise<Object>} Standardized response object
 */
export const apiRequest = async (method, url, data = null, config = {}) => {
  try {
    let response;
    const methodLower = method.toLowerCase();
    
    // Make the API request based on method
    switch (methodLower) {
      case HTTP_METHODS.GET:
        response = await apiClient.get(url, { ...config, params: data });
        break;
      case HTTP_METHODS.POST:
        response = await apiClient.post(url, data, config);
        break;
      case HTTP_METHODS.PUT:
        response = await apiClient.put(url, data, config);
        break;
      case HTTP_METHODS.PATCH:
        response = await apiClient.patch(url, data, config);
        break;
      case HTTP_METHODS.DELETE:
        response = await apiClient.delete(url, { ...config, data });
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    // Standardized success response
    return {
      success: true,
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      message: response.data?.message || 'Request successful',
      headers: response.headers,
      config: response.config,
    };

  } catch (error) {
    // Standardized error response
    return {
      success: false,
      status: error.response?.status || 0,
      statusText: error.response?.statusText || 'Unknown Error',
      data: error.response?.data || null,
      message: error.message || 'An unexpected error occurred',
      error: error,
      config: error.config || null,
    };
  }
};

/**
 * Utility functions for common API operations with standardized response handling
 */
export const apiUtils = {
  /**
   * Generic GET request
   * @param {string} url - API endpoint URL
   * @param {Object} params - Query parameters
   * @param {Object} config - Additional axios configuration
   * @returns {Promise<Object>} Standardized response object
   */
  get: (url, params = {}, config = {}) => apiRequest(HTTP_METHODS.GET, url, params, config),
  
  /**
   * Generic POST request
   * @param {string} url - API endpoint URL
   * @param {Object} data - Request data
   * @param {Object} config - Additional axios configuration
   * @returns {Promise<Object>} Standardized response object
   */
  post: (url, data = {}, config = {}) => apiRequest(HTTP_METHODS.POST, url, data, config),
  
  /**
   * Generic PUT request
   * @param {string} url - API endpoint URL
   * @param {Object} data - Request data
   * @param {Object} config - Additional axios configuration
   * @returns {Promise<Object>} Standardized response object
   */
  put: (url, data = {}, config = {}) => apiRequest(HTTP_METHODS.PUT, url, data, config),
  
  /**
   * Generic PATCH request
   * @param {string} url - API endpoint URL
   * @param {Object} data - Request data
   * @param {Object} config - Additional axios configuration
   * @returns {Promise<Object>} Standardized response object
   */
  patch: (url, data = {}, config = {}) => apiRequest(HTTP_METHODS.PATCH, url, data, config),
  
  /**
   * Generic DELETE request
   * @param {string} url - API endpoint URL
   * @param {Object} data - Request data
   * @param {Object} config - Additional axios configuration
   * @returns {Promise<Object>} Standardized response object
   */
  delete: (url, data = {}, config = {}) => apiRequest(HTTP_METHODS.DELETE, url, data, config),
  
  /**
   * Upload file with progress tracking
   * @param {string} url - API endpoint URL
   * @param {FormData} formData - File data to upload
   * @param {Function} onUploadProgress - Progress callback function
   * @param {Object} config - Additional axios configuration
   * @returns {Promise<Object>} Standardized response object
   */
  upload: (url, formData, onUploadProgress, config = {}) => {
    return apiRequest(HTTP_METHODS.POST, url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config.headers,
      },
      onUploadProgress,
    });
  },
  
  /**
   * Download file from server
   * @param {string} url - File download URL
   * @param {string} filename - Name for the downloaded file
   * @param {Object} config - Additional axios configuration
   * @returns {Promise<Object>} Standardized response object
   */
  download: async (url, filename, config = {}) => {
    try {
      const response = await apiClient.get(url, {
        ...config,
        responseType: 'blob',
      });
      
      // Create download link and trigger download
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return {
        success: true,
        status: response.status,
        data: null,
        message: 'File downloaded successfully',
      };
    } catch (error) {
      return {
        success: false,
        status: error.response?.status || 0,
        data: null,
        message: error.message || 'Download failed',
      };
    }
  },
};

/**
 * Configure interceptors with Redux dispatch for authentication handling
 * This function should be called once during app initialization with the Redux dispatch function
 * @param {Function} dispatch - Redux dispatch function
 */
export const configureInterceptors = (dispatch) => {
  // Clear existing response interceptors
  apiClient.interceptors.response.clear();
  
  // Set up new response interceptor with dispatch
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error.response?.status;
      
      captureHttpErrorIfNeeded(error, {
        tags: {
          errorType: 'axios_error',
          httpStatus: status || 'network_error',
        },
        contexts: {
          http: {
            method: error.config?.method?.toUpperCase(),
            url: error.config?.url,
            status_code: status,
            request_headers: error.config?.headers,
          },
        },
        extra: {
          responseData: error.response?.data,
          requestData: error.config?.data,
        },
      });

      // Handle authentication errors with dispatch
      if (status === HTTP_STATUS.UNAUTHORIZED || status === HTTP_STATUS.FORBIDDEN) {
        handleAuthFailure(dispatch);
        return Promise.reject(error);
      }
      
      // Handle other HTTP errors
      const enhancedError = handleHttpError(error);
      return Promise.reject(enhancedError);
    }
  );
};

export default apiClient;